<template>
  <div>
    <div class="w-full xl:w-5/12">
      <div>
        <div>
          <fwb-select
            v-model="selectedProvider"
            :options="providers"
            class="w-64"
            @change="cleanError('spaceUrl')"
            label="Telephony Provider" />
        </div>

        <div class="mt-4">
          <fwb-input
            v-model="accountSid"
            @input="cleanError('accountSid')"
            :validation-status="hasError('accountSid')"
            :label="accountSidLabel()">
            <template v-if="hasError('accountSid')" #validationMessage>
              {{ errorText('accountSid') }}
            </template>
          </fwb-input>
        </div>

        <div class="mt-4">
          <fwb-input
            v-model="authToken"
            @input="cleanError('authToken')"
            :validation-status="hasError('authToken')"
            label="Token">
            <template v-if="hasError('authToken')" #validationMessage>
              {{ errorText('authToken') }}
            </template>
          </fwb-input>
        </div>

        <div class="mt-4">
          <fwb-input
            v-if="selectedProvider === 'signalwire'"
            v-model="spaceUrl"
            @input="cleanError('spaceUrl')"
            :validation-status="hasError('spaceUrl')"
            label="Space URL">
            <template v-if="hasError('spaceUrl')" #validationMessage>
              {{ errorText('spaceUrl') }}
            </template>
          </fwb-input>
        </div>

        <div class="mt-4">
          <fwb-input
            v-if="selectedProvider === 'signalwire'"
            v-model="signingKey"
            @input="cleanError('signingKey')"
            :validation-status="hasError('signingKey')"
            label="Signing key">
            <template v-if="hasError('signingKey')" #validationMessage>
              {{ errorText('signingKey') }}
            </template>
          </fwb-input>
        </div>
      </div>
    </div>

    <div class="actions mt-10 border-t border-gray-200 pt-5">
      <fwb-button
        v-if="isNewAccount()"
        class="px-5 py-2.5 me-2 mb-2"
        color="default"
        @click="addAccount">

        Add Telephony Account
      </fwb-button>

      <fwb-button
        v-else
        class="px-5 py-2.5 me-2 mb-2"
        color="default"
        @click="updateAccount">

        Update Telephony Account
      </fwb-button>

      <a
        href="/telephony_accounts"
        class="inline-block w-full sm:w-auto text-center text-gray-900 bg-white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700">
        Cancel
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { FwbSelect, FwbInput, FwbButton } from 'flowbite-vue'
import { toastError, toastSuccess } from '~/javascript/utils/toast'
import securedAxios from '~/javascript/utils/axios'
import useValidation from '~/javascript/components/composables/validation'

const props = defineProps({
  telephonyAccount: {
    type: Object,
    required: true
  }
})

const axios = securedAxios()

const providers = [
  { value: 'twilio', name: 'Twilio' },
  { value: 'signalwire', name: 'SignalWire' }
]

const accountId = ref(props.telephonyAccount.id)
const accountSid = ref(props.telephonyAccount.account_sid || '')
const authToken = ref(props.telephonyAccount.auth_token || '')
const spaceUrl = ref(props.telephonyAccount.space_url || '')
const selectedProvider = ref(props.telephonyAccount.provider || 'twilio')
const signingKey = ref(props.telephonyAccount.signing_key || '')

// Validation errors from backend and form submitting state to show spinner
const { updateErrors, hasError, errorText, cleanError } = useValidation()

function accountSidLabel() {
  return selectedProvider.value === 'signalwire' ? 'Project ID' : 'Account SID'
}

function isNewAccount() {
  return !accountId.value
}

function collectParams() {
  return {
    account_sid: accountSid.value,
    auth_token: authToken.value,
    space_url: spaceUrl.value,
    provider: selectedProvider.value,
    signing_key: signingKey.value
  }
}

async function addAccount() {
  try {
    const params = collectParams()

    await axios.post('/ajax/telephony_accounts', params)
    toastSuccess('Telephony account added successfully!')

    setTimeout(() => {
      window.Turbo.visit('/telephony_accounts')
    }, 1500)
  } catch (error) {
    updateErrors(error.response.data)
    toastError(error)
  }
}

async function updateAccount() {
  try {
    const params = collectParams()

    await axios.patch(`/ajax/telephony_accounts/${accountId.value}`, params)
    toastSuccess('Telephony account added successfully!')

    setTimeout(() => {
      window.Turbo.visit('/telephony_accounts')
    }, 1500)
  } catch (error) {
    updateErrors(error.response.data)
    toastError(error)
  }
}
</script>

<style scoped>
</style>
